import React from 'react';
import { StepContainer } from '../StepContainer';
import { FormSection } from './form/formSection';
import { FormItem } from './form/formItem';

interface TechnicalStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const TechnicalStep: React.FC<TechnicalStepProps> = ({
  data = {
    electricite: {},
    plomberie: {},
    chauffage: {},
    ventilation: {},
    securiteConformite: {},
  },
  onChange,
  onNext,
  onBack
}) => {

  const updateSection = (section: string, key: string, value: any) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [key]: value
      }
    });
  };

  return (
    <StepContainer
      title="Équipements Techniques"
      description="Détaillez les travaux techniques à réaliser dans le bâtiment"
      onNext={onNext}
      onBack={onBack}
    >
      <div className="space-y-6">
        {/* Section Électricité */}
        <FormSection
          title="1. Électricité"
          description="Installation et mise aux normes électriques"
        >
          <div className="space-y-4">
            <FormItem
              label="Ajout d'un compteur électrique (création nouveau point de livraison)"
              value={data.electricite?.compteur || { selected: false }}
              onChange={(value) => updateSection('electricite', 'compteur', value)}
              withQuantity
            />
            <FormItem
              label="Remise aux normes électriques de l'installation"
              value={data.electricite?.remiseNormes || { selected: false }}
              onChange={(value) => updateSection('electricite', 'remiseNormes', value)}
              withQuantity
            />
            <FormItem
              label="Installation d'un tableau électrique neuf"
              value={data.electricite?.tableauNeuf || { selected: false }}
              onChange={(value) => updateSection('electricite', 'tableauNeuf', value)}
              withQuantity
            />
            <FormItem
              label="Ajout de dispositifs de sécurité (différentiel, disjoncteur)"
              value={data.electricite?.dispositifsSecurite || { selected: false }}
              onChange={(value) => updateSection('electricite', 'dispositifsSecurite', value)}
              withQuantity
            />
            
            <h4 className="text-sm font-medium text-neutral-700 mt-6 mb-3">
              Création ou modification de circuits électriques
            </h4>
            
            <FormItem
              label="Installation/remplacement de prises de courant"
              value={data.electricite?.prises || { selected: false, quantity: 0 }}
              onChange={(value) => updateSection('electricite', 'prises', value)}
              withQuantity={true}
            />
            <FormItem
              label="Installation/remplacement d'interrupteurs"
              value={data.electricite?.interrupteurs || { selected: false, quantity: 0 }}
              onChange={(value) => updateSection('electricite', 'interrupteurs', value)}
              withQuantity={true}
            />
            <FormItem
              label="Installation/remplacement de luminaires ou spots"
              value={data.electricite?.luminaires || { selected: false, quantity: 0 }}
              onChange={(value) => updateSection('electricite', 'luminaires', value)}
              withQuantity={true}
            />
            <FormItem
              label="Installation réseau télécom (fibre, interphone, etc)"
              value={data.electricite?.reseauTelecom || { selected: false }}
              onChange={(value) => updateSection('electricite', 'reseauTelecom', value)}
              withQuantity
            />
            <FormItem
              label="Installation d'un visiophone"
              value={data.electricite?.visiophone || { selected: false }}
              onChange={(value) => updateSection('electricite', 'visiophone', value)}
              withQuantity
            />
          </div>
        </FormSection>

        {/* Section Plomberie */}
        <FormSection
          title="2. Plomberie"
          description="Installation et rénovation de la plomberie"
        >
          <div className="space-y-4">
            <FormItem
              label="Création ou modification de réseaux d'alimentation en eau"
              value={data.plomberie?.reseauxAlimentation || { selected: false }}
              onChange={(value) => updateSection('plomberie', 'reseauxAlimentation', value)}
              withLength
            />
            <FormItem
              label="Création ou modification de réseaux d'évacuation"
              value={data.plomberie?.reseauxEvacuation || { selected: false }}
              onChange={(value) => updateSection('plomberie', 'reseauxEvacuation', value)}
              withLength
            />
            <FormItem
              label="Installation d'un système de traitement de l'eau (adoucisseur)"
              value={data.plomberie?.traitementEau || { selected: false }}
              onChange={(value) => updateSection('plomberie', 'traitementEau', value)}
              withQuantity
            />
            <FormItem
              label="Installation ou remplacement d'un chauffe-eau électrique"
              value={data.plomberie?.chauffeEauElectrique || { selected: false }}
              onChange={(value) => updateSection('plomberie', 'chauffeEauElectrique', value)}
              withQuantity
            />
            <FormItem
              label="Installation ou remplacement d'un chauffe-eau thermodynamique"
              value={data.plomberie?.chauffeEauThermo || { selected: false }}
              onChange={(value) => updateSection('plomberie', 'chauffeEauThermo', value)}
              withQuantity
            />
            <FormItem
              label="Nombre de points d'eau à installer/rénover"
              value={data.plomberie?.pointsEau || { selected: false, quantity: 0 }}
              onChange={(value) => updateSection('plomberie', 'pointsEau', value)}
              withQuantity={true}
            />
          </div>
        </FormSection>

        {/* Section Chauffage */}
        <FormSection
          title="3. Chauffage"
          description="Installation et rénovation du système de chauffage"
        >
          <div className="space-y-4">
            <FormItem
              label="Installation d'une chaudière gaz"
              value={data.chauffage?.chaudiereGaz || { selected: false }}
              onChange={(value) => updateSection('chauffage', 'chaudiereGaz', value)}
            />
            <FormItem
              label="Installation d'une chaudière fioul"
              value={data.chauffage?.chaudiereFioul || { selected: false }}
              onChange={(value) => updateSection('chauffage', 'chaudiereFioul', value)}
            />
            <FormItem
              label="Installation d'une pompe à chaleur air/air"
              value={data.chauffage?.pacAirAir || { selected: false }}
              onChange={(value) => updateSection('chauffage', 'pacAirAir', value)}
            />
            <FormItem
              label="Installation d'une pompe à chaleur air/eau"
              value={data.chauffage?.pacAirEau || { selected: false }}
              onChange={(value) => updateSection('chauffage', 'pacAirEau', value)}
            />
            <FormItem
              label="Installation d'une pompe à chaleur eau/eau"
              value={data.chauffage?.pacEauEau || { selected: false }}
              onChange={(value) => updateSection('chauffage', 'pacEauEau', value)}
            />
            <FormItem
              label="Installation de radiateurs"
              value={data.chauffage?.radiateurs || { selected: false, quantity: 0 }}
              onChange={(value) => updateSection('chauffage', 'radiateurs', value)}
              withQuantity={true}
            />
            <FormItem
              label="Suppression d'une cuve fioul"
              value={data.chauffage?.suppressionCuve || { selected: false }}
              onChange={(value) => updateSection('chauffage', 'suppressionCuve', value)}
            />
            <FormItem
              label="Chaudière XXL sur devis ou autre type de chauffe"
              value={data.chauffage?.chauffagesurmesure || { selected: false }}
              onChange={(value) => updateSection('chauffage', 'chauffagesurmesure', value)}
              withCustomAmount={true}
            />
          </div>
        </FormSection>

        {/* Section Ventilation */}
        <FormSection
          title="4. Ventilation"
          description="Installation et rénovation de la ventilation"
        >
          <div className="space-y-4">
            <FormItem
              label="Installation ou rénovation d'une VMC simple flux"
              value={data.ventilation?.vmcSimple || { selected: false }}
              onChange={(value) => updateSection('ventilation', 'vmcSimple', value)}
            />
            <FormItem
              label="Installation ou rénovation d'une VMC double flux"
              value={data.ventilation?.vmcDouble || { selected: false }}
              onChange={(value) => updateSection('ventilation', 'vmcDouble', value)}
            />
            <FormItem
              label="Nombre de bouches d'extraction à installer"
              value={data.ventilation?.bouches || { selected: false, quantity: 0 }}
              onChange={(value) => updateSection('ventilation', 'bouches', value)}
              withQuantity={true}
            />
            <FormItem
              label="Installation d'une ventilation naturelle"
              value={data.ventilation?.ventilationNaturelle || { selected: false }}
              onChange={(value) => updateSection('ventilation', 'ventilationNaturelle', value)}
              withQuantity
            />
            <FormItem
              label="Travail d'amélioration de la ventilation existante"
              value={data.ventilation?.ameliorationExistant || { selected: false }}
              onChange={(value) => updateSection('ventilation', 'ameliorationExistant', value)}
              withQuantity
            />
          </div>
        </FormSection>

        {/* Section Sécurité et conformité */}
        <FormSection
          title="5. Sécurité et conformité"
          description="Diagnostics et mise en conformité"
        >
          <div className="space-y-4">
            <FormItem
              label="Diagnostic Consuel pour conformité électrique"
              value={data.securiteConformite?.consuel || { selected: false }}
              onChange={(value) => updateSection('securiteConformite', 'consuel', value)}
              withQuantity
            />
            <FormItem
              label="Diagnostic installation gaz"
              value={data.securiteConformite?.diagnosticGaz || { selected: false }}
              onChange={(value) => updateSection('securiteConformite', 'diagnosticGaz', value)}
              withQuantity
            />
            <FormItem
              label="Installation de détecteurs (fumée, monoxyde carbone)"
              value={data.securiteConformite?.detecteurs || { selected: false }}
              onChange={(value) => updateSection('securiteConformite', 'detecteurs', value)}
              withQuantity
            />
          </div>
        </FormSection>

      </div>
    </StepContainer>
  );
};

export default TechnicalStep;